import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, IconButton, Grid, Paper, Button, Chip, Tooltip,
  Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Switch, FormControlLabel, Select, MenuItem,
  FormControl, InputLabel, CircularProgress, Snackbar,
  Table, TableBody, TableCell, TableHead, TableRow, Checkbox,
  Tabs, Tab, Divider, Icon,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import * as Icons from '@material-ui/icons';
import GridLayout from 'react-grid-layout';
const { WidthProvider } = GridLayout;
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useHistory, useParams } from 'react-router-dom';
import { invokeApig } from '../libs/awsLib';
import { useDesignManager } from '../hooks/useDesignManager';
import {
  SECTION_TYPES, FIELD_TYPES, DISPLAY_OPTIONS, PADDING_OPTIONS,
  getSectionType, getDefaultDisplay, DETAIL_TEMPLATES, DETAIL_TEMPLATE_FIELDS,
} from '../config/contentTypes';

const RGL = WidthProvider(GridLayout);

// ── Utilidades ──────────────────────────────────────────────────────────────

function labelToKey(label) {
  return label
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function generateSectionId(type) {
  return `section_${type}_${Date.now()}`;
}

function getDisplayLabel(value) {
  return DISPLAY_OPTIONS.find((o) => o.value === value)?.label || value;
}

const DEFAULT_H = {
  hero_banner: 3, card: 2, video: 2,
  text_block: 2, gallery: 2, contact_form: 2, footer: 1,
  product_cards: 3, service_cards: 3, team_cards: 3,
  testimonials: 2, faq_accordion: 3, search_bar: 1,
  visual_cards: 3, detail_block: 4,
  heading_h1: 1, heading_h2: 1, heading_h3: 1,
  paragraph_text: 2, text_label: 1,
};

function getDefaultH(type) {
  return DEFAULT_H[type] || 2;
}

// Migra secciones del formato antiguo (rowId/colSpan/order) al nuevo (x/y/w/h)
function migrateToGrid(secs) {
  if (!secs || secs.length === 0) return secs;
  if (secs[0].layout?.x !== undefined) return secs;

  const sorted = [...secs].sort((a, b) => a.order - b.order);
  const rowMap  = {};
  const rowOrder = [];
  sorted.forEach((s) => {
    const rid = s.layout?.rowId || s.id;
    if (!rowMap[rid]) { rowMap[rid] = []; rowOrder.push(rid); }
    rowMap[rid].push(s);
  });

  return secs.map((s) => {
    const rowId   = s.layout?.rowId || s.id;
    const y       = rowOrder.indexOf(rowId) * 3;
    const rowSecs = [...(rowMap[rowId] || [])].sort((a, b) => a.order - b.order);
    const myIdx   = rowSecs.findIndex((sec) => sec.id === s.id);
    let x = 0;
    for (let i = 0; i < myIdx; i++) x += rowSecs[i].layout?.colSpan || 12;
    return {
      ...s,
      layout: {
        display:       s.layout?.display,
        paddingTop:    s.layout?.paddingTop    || 'md',
        paddingBottom: s.layout?.paddingBottom || 'md',
        x,
        y,
        w: s.layout?.colSpan || 12,
        h: getDefaultH(s.type),
      },
    };
  });
}

// ── Estilos ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    margin:       theme.spacing(3, 0),
    marginBottom: theme.spacing(10),
    width:        'inherit',
  },
  toolbar: {
    display:      'flex',
    alignItems:   'center',
    padding:      theme.spacing(2),
    gap:          theme.spacing(1),
    borderBottom: '1px solid #e0e0e0',
  },
  toolbarTitle: { flexGrow: 1 },
  pageTabsBar:  { borderBottom: '1px solid #e0e0e0', marginBottom: theme.spacing(2) },
  noPages:      { textAlign: 'center', padding: theme.spacing(5, 2) },

  // ── Canvas ──
  canvasWrap: {
    backgroundColor: '#eeeeee',
    borderRadius:    8,
    border:          '1px solid #e0e0e0',
    padding:         8,
    minHeight:       320,
    // Líneas verticales de referencia (12 columnas)
    backgroundImage: `repeating-linear-gradient(
      90deg,
      transparent 0,
      transparent calc(8.333% - 1px),
      rgba(0,0,0,0.06) calc(8.333% - 1px),
      rgba(0,0,0,0.06) 8.333%
    )`,
  },
  emptyCanvas: {
    textAlign: 'center',
    padding:   theme.spacing(8, 2),
    color:     '#9e9e9e',
    pointerEvents: 'none',
  },

  // ── Card de sección dentro del canvas ──
  sectionCard: {
    height:          '100%',
    border:          '2px solid #e0e0e0',
    borderRadius:    8,
    backgroundColor: '#fff',
    display:         'flex',
    flexDirection:   'column',
    overflow:        'hidden',
    transition:      'border-color 0.15s, box-shadow 0.15s',
    '&:hover': {
      borderColor: '#9c27b0',
      boxShadow:   '0 2px 12px rgba(156,39,176,0.15)',
    },
    // Resize handle (esquina inferior derecha)
    '& .react-resizable-handle-se': {
      position:   'absolute',
      width:       20,
      height:      20,
      bottom:      0,
      right:       0,
      cursor:      'se-resize',
      background:  'none',
      '&::after': {
        content:     '""',
        position:    'absolute',
        right:       4,
        bottom:      4,
        width:       8,
        height:      8,
        borderRight: '2px solid #bdbdbd',
        borderBottom:'2px solid #bdbdbd',
        borderRadius:1,
        transition:  'border-color 0.15s',
      },
    },
    '&:hover .react-resizable-handle-se::after': {
      borderColor: '#9c27b0',
    },
  },

  // Barra superior del card — es el drag handle
  dragBar: {
    display:      'flex',
    alignItems:   'center',
    padding:      '5px 8px',
    background:   'linear-gradient(180deg, #fafafa 0%, #f0f0f0 100%)',
    borderBottom: '1px solid #ececec',
    cursor:       'grab',
    gap:          4,
    flexShrink:   0,
    userSelect:   'none',
    '&:active':   { cursor: 'grabbing' },
  },
  dragIcon:      { color: '#c5c5c5', fontSize: 16 },
  cardTitle: {
    flexGrow:     1,
    fontWeight:   600,
    fontSize:     12,
    overflow:     'hidden',
    textOverflow: 'ellipsis',
    whiteSpace:   'nowrap',
    color:        '#333',
  },
  cardBody: {
    padding:  '6px 8px',
    flexGrow: 1,
    overflow: 'hidden',
  },
  chipRow: {
    display:      'flex',
    flexWrap:     'wrap',
    gap:          3,
    marginBottom: 3,
  },
  listChip:       { backgroundColor: '#e3f2fd', color: '#1565c0', fontSize: 10, height: 16 },
  uniqueChip:     { backgroundColor: '#fce4ec', color: '#880e4f', fontSize: 10, height: 16 },
  displayChip:    { backgroundColor: '#f3e5f5', color: '#6a1b9a', fontSize: 10, height: 16 },
  containerChip:  { backgroundColor: '#e8f5e9', color: '#1b5e20', fontSize: 10, height: 16 },
  typographyChip: { backgroundColor: '#fff3e0', color: '#e65100', fontSize: 10, height: 16 },
  fieldChip: {
    fontFamily:      'monospace',
    fontSize:        10,
    backgroundColor: '#e8f4fd',
    color:           '#1565c0',
    border:          '1px solid #90caf9',
    height:          16,
    margin:          '1px 1px',
  },

  // ── Dialog ──
  typeGrid: {
    display:             'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap:                 theme.spacing(1.5),
    marginTop:           theme.spacing(1),
  },
  typeCard: {
    border:       '2px solid #e0e0e0',
    borderRadius: 8,
    padding:      theme.spacing(1.5),
    textAlign:    'center',
    cursor:       'pointer',
    transition:   'all 0.2s',
    '&:hover':    { borderColor: '#9c27b0', backgroundColor: '#f3e5f5' },
  },
  typeCardSelected: { borderColor: '#9c27b0', backgroundColor: '#f3e5f5' },
  typeCardIcon:     { fontSize: 32, color: '#9c27b0', marginBottom: 4 },
  layoutBtnGroup:   { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 },
  layoutBtn:        { minWidth: 0, padding: '2px 10px', fontSize: 12 },
  addFieldRow: {
    backgroundColor: '#f9fbe7',
    border:          '1px dashed #c5e1a5',
    borderRadius:    4,
    padding:         theme.spacing(1.5),
    marginTop:       theme.spacing(1),
  },
  stepTitle: { color: '#9c27b0', fontWeight: 600, marginBottom: theme.spacing(2) },

  // ── Vista previa WYSIWYG ──
  previewCanvas: {
    backgroundColor: '#f5f5f5',
    borderRadius:    8,
    border:          '1px solid #e0e0e0',
    minHeight:       320,
    overflow:        'hidden',
  },
  previewSection: {
    position: 'relative',
    '&:hover $previewOverlay': { opacity: 1 },
  },
  previewOverlay: {
    position:   'absolute',
    top:        8,
    right:      8,
    opacity:    0,
    transition: 'opacity 0.2s',
    display:    'flex',
    gap:        4,
    zIndex:     2,
  },
  colorInputRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        8,
    marginTop:  4,
  },
  modeToggleBtn: {
    borderRadius:  20,
    padding:       '4px 16px',
    textTransform: 'none',
    fontWeight:    600,
  },
}));

// ── Componente principal ─────────────────────────────────────────────────────

function GuidedDesignerPage() {
  const { designId } = useParams();
  const history      = useHistory();
  const classes      = useStyles();
  const { saveDesign, loadDesign, listDesigns, deleteDesign, isSaving, isLoading, isListing } = useDesignManager(designId);

  // Estado principal
  const [designName, setDesignName]             = useState('');
  const [pages, setPages]                       = useState([]);
  const [currentPageId, setCurrentPageId]       = useState(null);
  const [currentPageTitle, setCurrentPageTitle] = useState('');
  const [sections, setSections]                 = useState([]);
  const [tabIndex, setTabIndex]                 = useState(0);
  const [isLoadingInfo, setIsLoadingInfo]       = useState(true);

  // Toast
  const [openToast, setOpenToast]         = useState(false);
  const [toastMsg, setToastMsg]           = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');

  // Dialog nueva página
  const [openNewPage, setOpenNewPage]       = useState(false);
  const [newPageTitle, setNewPageTitle]     = useState('');
  const [isCreatingPage, setIsCreatingPage] = useState(false);
  const [newPageError, setNewPageError]     = useState('');

  // Dialog eliminar página
  const [openDeletePage, setOpenDeletePage]   = useState(false);
  const [isDeletingPage, setIsDeletingPage]   = useState(false);

  // Dialog agregar / editar sección
  const [openSectionDialog, setOpenSectionDialog]   = useState(false);
  const [sectionStep, setSectionStep]               = useState(0);
  const [selectedType, setSelectedType]             = useState(null);
  const [sectionLabel, setSectionLabel]             = useState('');
  const [sectionIsList, setSectionIsList]           = useState(true);
  const [sectionFields, setSectionFields]           = useState([]);
  const [editingSectionId, setEditingSectionId]     = useState(null);
  const [sectionDialogError, setSectionDialogError] = useState('');
  const [layoutDisplay, setLayoutDisplay]           = useState('grid_3');
  const [layoutPaddingTop, setLayoutPaddingTop]     = useState('md');
  const [layoutPaddingBottom, setLayoutPaddingBottom] = useState('md');
  const [layoutBgColor, setLayoutBgColor]           = useState('');
  const [layoutTextColor, setLayoutTextColor]       = useState('');
  const [layoutBorderStyle, setLayoutBorderStyle]   = useState('none');
  const [layoutBorderWidth, setLayoutBorderWidth]   = useState(1);
  const [layoutBorderColor, setLayoutBorderColor]   = useState('#e0e0e0');
  const [layoutBorderRadius, setLayoutBorderRadius] = useState(0);
  const [layoutAlignment, setLayoutAlignment]       = useState('center');
  const [layoutContainerWidth, setLayoutContainerWidth] = useState('wide');
  const [layoutMinHeight, setLayoutMinHeight]       = useState('auto');
  const [layoutTitleSize, setLayoutTitleSize]       = useState('md');
  const [dialogTab, setDialogTab]                   = useState(0);
  const [previewMode, setPreviewMode]               = useState(false);
  const [pageStyle, setPageStyle]                   = useState({ backgroundColor: '', backgroundImage: '' });
  const [openPageStyle, setOpenPageStyle]           = useState(false);
  const [hoverType, setHoverType]                   = useState(null);

  // Páginas y página de detalle para secciones tipo lista
  const [allPages, setAllPages]                       = useState([]);
  const [sectionDetailPageId, setSectionDetailPageId] = useState('');
  const [sectionDetailTemplate, setSectionDetailTemplate] = useState('article');

  // Agregar campo
  const [showAddField, setShowAddField]         = useState(false);
  const [newFieldLabel, setNewFieldLabel]       = useState('');
  const [newFieldType, setNewFieldType]         = useState('text');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [newFieldError, setNewFieldError]       = useState('');

  // ── Carga inicial ────────────────────────────────────────────────────────

  useEffect(() => {
    loadInitialData();
  }, [designId]); // eslint-disable-line

  const loadInitialData = async () => {
    setIsLoadingInfo(true);
    try {
      const designRes = await invokeApig({ path: '/design/find', method: 'POST', body: { designId } });
      if (designRes.Status === 0 || designRes.statusCode === 200) {
        setDesignName(designRes.design?.name || '');
      }
      const listRes = await listDesigns();
      if (listRes.success) {
        const allPagesArr = listRes.pages || [];
        const guided = allPagesArr.filter((p) => p.pageType === 'guided');
        setPages(guided);
        setAllPages(allPagesArr);
        if (guided.length > 0) await loadPage(guided[0].pageId);
      }
    } catch (e) {
      showToast('Error al cargar el diseño', 'error');
    } finally {
      setIsLoadingInfo(false);
    }
  };

  const loadPage = useCallback(async (pageId) => {
    const result = await loadDesign(pageId);
    if (result.success) {
      setCurrentPageId(result.pageId);
      setCurrentPageTitle(result.pageInfo?.title || '');
      setSections(migrateToGrid(result.metadata?.guidedSections || []));
      setPageStyle(result.metadata?.pageStyle || { backgroundColor: '', backgroundImage: '' });
    }
  }, [loadDesign]);

  const handleTabChange = async (_, newIndex) => {
    setTabIndex(newIndex);
    await loadPage(pages[newIndex].pageId);
  };

  // ── Guardar ──────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (sections.length === 0) { showToast('Agrega al menos una sección antes de guardar', 'warning'); return; }

    // Recalcular order según posición visual (y → x) antes de guardar
    const orderedSections = [...sections]
      .sort((a, b) => {
        const ay = a.layout?.y ?? 0, by = b.layout?.y ?? 0;
        if (ay !== by) return ay - by;
        return (a.layout?.x ?? 0) - (b.layout?.x ?? 0);
      })
      .map((s, i) => ({ ...s, order: i }));

    const result = await saveDesign(
      { mode: 'guided' },
      { mode: 'guided', guidedSections: orderedSections, pageStyle },
      currentPageId,
      currentPageTitle,
      'guided',
    );
    if (result.success) {
      if (!currentPageId) { setCurrentPageId(result.data.pageId); await loadInitialData(); }
      showToast('Página guardada correctamente');
    } else {
      showToast('Error al guardar la página', 'error');
    }
  };

  // ── Nueva página ─────────────────────────────────────────────────────────

  const handleCreatePage = async () => {
    if (!newPageTitle.trim()) { setNewPageError('El nombre de la página es requerido'); return; }
    setIsCreatingPage(true);
    setNewPageError('');
    const result = await saveDesign(
      { mode: 'guided' },
      { mode: 'guided', guidedSections: [] },
      null, newPageTitle.trim(), 'guided',
    );
    setIsCreatingPage(false);
    if (result.success) {
      setOpenNewPage(false);
      setNewPageTitle('');
      showToast('Página creada correctamente');
      await loadInitialData();
      setCurrentPageId(result.data.pageId);
      setCurrentPageTitle(newPageTitle.trim());
      setSections([]);
    } else {
      setNewPageError('Error al crear la página. Intenta nuevamente.');
    }
  };

  // ── Eliminar página ──────────────────────────────────────────────────────

  const handleDeletePage = async () => {
    if (!currentPageId) return;
    setIsDeletingPage(true);
    const result = await deleteDesign(currentPageId);
    setIsDeletingPage(false);
    if (result.success) {
      setOpenDeletePage(false);
      const remaining = pages.filter((p) => p.pageId !== currentPageId);
      if (remaining.length > 0) {
        await loadInitialData();
        setCurrentPageId(remaining[0].pageId);
        setCurrentPageTitle(remaining[0].title);
        setSections([]);
        await loadPage(remaining[0].pageId);
      } else {
        await loadInitialData();
        setCurrentPageId(null);
        setCurrentPageTitle('');
        setSections([]);
        setTabIndex(0);
      }
      showToast('Página eliminada');
    } else {
      showToast('Error al eliminar la página', 'error');
    }
  };

  // ── Canvas: cambio de posición / tamaño ──────────────────────────────────

  const handleLayoutStop = useCallback((newLayout) => {
    setSections((prev) => prev.map((s) => {
      const item = newLayout.find((l) => l.i === s.id);
      if (!item) return s;
      return { ...s, layout: { ...s.layout, x: item.x, y: item.y, w: item.w, h: item.h } };
    }));
  }, []);

  // ── Secciones — agregar / editar ─────────────────────────────────────────

  const handleOpenAddSection = () => {
    setEditingSectionId(null);
    setSelectedType(null);
    setSectionLabel('');
    setSectionIsList(true);
    setSectionFields([]);
    setSectionStep(0);
    setSectionDialogError('');
    setShowAddField(false);
    setLayoutDisplay('grid_3');
    setLayoutPaddingTop('md');
    setLayoutPaddingBottom('md');
    setLayoutBgColor('');
    setLayoutTextColor('');
    setLayoutBorderStyle('none');
    setLayoutBorderWidth(1);
    setLayoutBorderColor('#e0e0e0');
    setLayoutBorderRadius(0);
    setLayoutAlignment('center');
    setLayoutContainerWidth('wide');
    setLayoutMinHeight('auto');
    setLayoutTitleSize('md');
    setSectionDetailPageId('');
    setSectionDetailTemplate('article');
    setDialogTab(0);
    setOpenSectionDialog(true);
  };

  const handleOpenEditSection = (section) => {
    const sType = getSectionType(section.type);
    setEditingSectionId(section.id);
    setSelectedType(sType);
    setSectionLabel(section.label);
    setSectionIsList(section.isList);
    setSectionFields(section.fields.map((f) => ({ ...f })));
    setSectionStep(1);
    setSectionDialogError('');
    setShowAddField(false);
    resetNewField();
    const layout = section.layout || {};
    setLayoutDisplay(layout.display || getDefaultDisplay(section.type, section.isList));
    setLayoutPaddingTop(layout.paddingTop || 'md');
    setLayoutPaddingBottom(layout.paddingBottom || 'md');
    setLayoutBgColor(layout.backgroundColor || '');
    setLayoutTextColor(layout.textColor || '');
    setLayoutBorderStyle(layout.borderStyle || 'none');
    setLayoutBorderWidth(layout.borderWidth || 1);
    setLayoutBorderColor(layout.borderColor || '#e0e0e0');
    setLayoutBorderRadius(layout.borderRadius || 0);
    setLayoutAlignment(layout.alignment || 'center');
    setLayoutContainerWidth(layout.containerWidth || 'wide');
    setLayoutMinHeight(layout.minHeight || 'auto');
    setLayoutTitleSize(layout.titleSize || 'md');
    setSectionDetailPageId(section.detailPageId || '');
    const tmpl = section.layout?.template || 'article';
    setSectionDetailTemplate(tmpl);
    if (sType?.isDetailTemplate) {
      const tmplFields = DETAIL_TEMPLATE_FIELDS[tmpl] || [];
      const savedFields = section.fields || [];
      setSectionFields((savedFields.length > 0 ? savedFields : tmplFields).map((f) => ({ ...f })));
    }
    setDialogTab(0);
    setOpenSectionDialog(true);
  };

  const handleSelectType = (sType) => {
    setSelectedType(sType);
    setSectionLabel(sType.label);
    setSectionIsList(sType.canBelist);
    setLayoutDisplay(sType.defaultDisplay || getDefaultDisplay(sType.value, sType.canBelist));
    setSectionDetailPageId('');
    if (sType.isDetailTemplate) {
      setSectionDetailTemplate('article');
      setSectionFields((DETAIL_TEMPLATE_FIELDS.article || []).map((f) => ({ ...f })));
    } else {
      setSectionDetailTemplate('article');
      setSectionFields(sType.defaultFields.map((f) => ({ ...f })));
    }
  };

  const handleNextStep = () => {
    if (!selectedType) { setSectionDialogError('Selecciona un tipo de sección'); return; }
    setSectionDialogError('');
    setSectionStep(1);
  };

  const handleConfirmSection = () => {
    if (!sectionLabel.trim()) { setSectionDialogError('El nombre de la sección es requerido'); return; }
    if (!selectedType?.isDetailTemplate && sectionFields.length === 0) { setSectionDialogError('Agrega al menos un campo'); return; }

    const existing = editingSectionId ? sections.find((s) => s.id === editingSectionId) : null;

    // Posición para sección nueva: abajo de todo lo existente
    const maxY = sections.length === 0 ? 0
      : Math.max(...sections.map((s) => (s.layout?.y || 0) + (s.layout?.h || getDefaultH(s.type))));

    const newSection = {
      id:     editingSectionId || generateSectionId(selectedType.value),
      type:   selectedType.value,
      label:  sectionLabel.trim(),
      isList: selectedType.canBelist ? sectionIsList : false,
      order:  editingSectionId ? (existing?.order ?? sections.length) : sections.length,
      fields: sectionFields,
      detailPageId:   sectionDetailPageId || null,
      detailPageSlug: sectionDetailPageId
        ? (allPages.find((p) => p.pageId === sectionDetailPageId)?.slug || null)
        : null,
      layout: {
        display:         sectionIsList ? layoutDisplay : 'single',
        paddingTop:      layoutPaddingTop,
        paddingBottom:   layoutPaddingBottom,
        x:               existing?.layout?.x ?? 0,
        y:               existing?.layout?.y ?? maxY,
        w:               existing?.layout?.w ?? 12,
        h:               existing?.layout?.h ?? getDefaultH(selectedType.value),
        backgroundColor: layoutBgColor,
        textColor:       layoutTextColor,
        borderStyle:     layoutBorderStyle,
        borderWidth:     layoutBorderWidth,
        borderColor:     layoutBorderColor,
        borderRadius:    layoutBorderRadius,
        alignment:       layoutAlignment,
        containerWidth:  layoutContainerWidth,
        minHeight:       layoutMinHeight,
        titleSize:       layoutTitleSize,
        template:        selectedType?.isDetailTemplate ? sectionDetailTemplate : undefined,
      },
    };

    if (editingSectionId) {
      setSections((prev) => prev.map((s) => s.id === editingSectionId ? newSection : s));
    } else {
      setSections((prev) => [...prev, newSection]);
    }
    setOpenSectionDialog(false);
  };

  const handleDeleteSection = (sectionId) => {
    setSections((prev) => prev.filter((s) => s.id !== sectionId));
  };

  // ── Campos ───────────────────────────────────────────────────────────────

  const resetNewField = () => {
    setNewFieldLabel(''); setNewFieldType('text'); setNewFieldRequired(false);
    setNewFieldError(''); setShowAddField(false);
  };

  const handleAddField = () => {
    if (!newFieldLabel.trim()) { setNewFieldError('El nombre del campo es requerido'); return; }
    const key = labelToKey(newFieldLabel.trim());
    if (sectionFields.some((f) => f.key === key)) { setNewFieldError(`Ya existe un campo con la clave "${key}"`); return; }
    setSectionFields((prev) => [...prev, { key, label: newFieldLabel.trim(), type: newFieldType, required: newFieldRequired }]);
    resetNewField();
  };

  const handleDeleteField    = (key) => setSectionFields((prev) => prev.filter((f) => f.key !== key));
  const handleToggleRequired = (key) => setSectionFields((prev) => prev.map((f) => f.key === key ? { ...f, required: !f.required } : f));

  // ── Toast ────────────────────────────────────────────────────────────────

  const showToast = (msg, severity = 'success') => {
    setToastMsg(msg); setToastSeverity(severity); setOpenToast(true);
  };

  // ── Apariencia: utilidades de borde ─────────────────────────────────────

  const getSectionBorderCSS = (layout) => {
    if (!layout?.borderStyle || layout.borderStyle === 'none') return {};
    const bw = `${layout.borderWidth || 1}px solid ${layout.borderColor || '#e0e0e0'}`;
    if (layout.borderStyle === 'all')    return { border: bw };
    if (layout.borderStyle === 'top')    return { borderTop: bw };
    if (layout.borderStyle === 'bottom') return { borderBottom: bw };
    if (layout.borderStyle === 'tb')     return { borderTop: bw, borderBottom: bw };
    return {};
  };

  const PADDING_PX = { none: 0, sm: 16, md: 40, lg: 72 };

  // ── Render: panel Apariencia en el dialog ────────────────────────────────

  const renderAppearanceOptions = () => {
    const BORDER_STYLES = [
      { value: 'none',   label: 'Sin borde' },
      { value: 'all',    label: 'Todos' },
      { value: 'top',    label: 'Superior' },
      { value: 'bottom', label: 'Inferior' },
      { value: 'tb',     label: 'Sup/Inf' },
    ];
    const BORDER_WIDTHS = [1, 2, 3, 4];
    const BORDER_RADII  = [
      { value: 0,  label: 'Sin redondeo' },
      { value: 4,  label: 'Suave' },
      { value: 8,  label: 'Medio' },
      { value: 16, label: 'Grande' },
      { value: 50, label: 'Píldora' },
    ];

    const colorField = (label, value, setter) => (
      <Grid item xs={12} sm={6}>
        <Typography variant="caption" display="block" style={{ marginBottom: 4, color: '#757575' }}>{label}</Typography>
        <div className={classes.colorInputRow}>
          <input
            type="color"
            value={value || '#ffffff'}
            onChange={(e) => setter(e.target.value)}
            style={{ width: 36, height: 36, border: '1px solid #e0e0e0', cursor: 'pointer', borderRadius: 4, padding: 2, background: 'none' }}
          />
          <TextField
            size="small"
            value={value}
            onChange={(e) => setter(e.target.value)}
            placeholder="vacío = por defecto"
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
            style={{ flex: 1 }}
          />
          {value && (
            <IconButton size="small" onClick={() => setter('')} style={{ color: '#9e9e9e' }}>
              <Icons.Close fontSize="small" />
            </IconButton>
          )}
        </div>
      </Grid>
    );

    const ALIGNMENTS = [
      { value: 'left',   label: 'Izquierda', icon: 'format_align_left' },
      { value: 'center', label: 'Centro',    icon: 'format_align_center' },
      { value: 'right',  label: 'Derecha',   icon: 'format_align_right' },
    ];
    const CONTAINER_WIDTHS = [
      { value: 'full',   label: '100%',    desc: 'Ancho completo' },
      { value: 'wide',   label: '1200px',  desc: 'Amplio' },
      { value: 'medium', label: '900px',   desc: 'Medio' },
      { value: 'narrow', label: '600px',   desc: 'Angosto' },
    ];
    const MIN_HEIGHTS = [
      { value: 'auto',   label: 'Auto' },
      { value: 'sm',     label: '200px' },
      { value: 'md',     label: '350px' },
      { value: 'lg',     label: '500px' },
      { value: 'screen', label: 'Pantalla' },
    ];
    const TITLE_SIZES = [
      { value: 'sm', label: 'Pequeño' },
      { value: 'md', label: 'Normal' },
      { value: 'lg', label: 'Grande' },
      { value: 'xl', label: 'Muy grande' },
    ];

    return (
      <Grid container spacing={2}>

        <Grid item xs={12}>
          <Typography variant="caption" display="block" style={{ marginBottom: 6, color: '#757575', fontWeight: 600 }}>
            Posicionamiento y Layout
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="caption" display="block" style={{ marginBottom: 4, color: '#757575' }}>
            Alineación del contenido
          </Typography>
          <div className={classes.layoutBtnGroup}>
            {ALIGNMENTS.map((opt) => (
              <Button key={opt.value} size="small" className={classes.layoutBtn}
                variant={layoutAlignment === opt.value ? 'contained' : 'outlined'}
                color={layoutAlignment === opt.value ? 'primary' : 'default'}
                onClick={() => setLayoutAlignment(opt.value)}
                startIcon={<Icon style={{ fontSize: 16 }}>{opt.icon}</Icon>}
              >{opt.label}</Button>
            ))}
          </div>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="caption" display="block" style={{ marginBottom: 4, color: '#757575' }}>
            Ancho del contenedor
          </Typography>
          <div className={classes.layoutBtnGroup}>
            {CONTAINER_WIDTHS.map((opt) => (
              <Tooltip key={opt.value} title={opt.desc} enterDelay={300}>
                <Button size="small" className={classes.layoutBtn}
                  variant={layoutContainerWidth === opt.value ? 'contained' : 'outlined'}
                  color={layoutContainerWidth === opt.value ? 'primary' : 'default'}
                  onClick={() => setLayoutContainerWidth(opt.value)}
                >{opt.label}</Button>
              </Tooltip>
            ))}
          </div>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="caption" display="block" style={{ marginBottom: 4, color: '#757575' }}>
            Altura mínima de la sección
          </Typography>
          <div className={classes.layoutBtnGroup}>
            {MIN_HEIGHTS.map((opt) => (
              <Button key={opt.value} size="small" className={classes.layoutBtn}
                variant={layoutMinHeight === opt.value ? 'contained' : 'outlined'}
                color={layoutMinHeight === opt.value ? 'primary' : 'default'}
                onClick={() => setLayoutMinHeight(opt.value)}
              >{opt.label}</Button>
            ))}
          </div>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="caption" display="block" style={{ marginBottom: 4, color: '#757575' }}>
            Tamaño del título
          </Typography>
          <div className={classes.layoutBtnGroup}>
            {TITLE_SIZES.map((opt) => (
              <Button key={opt.value} size="small" className={classes.layoutBtn}
                variant={layoutTitleSize === opt.value ? 'contained' : 'outlined'}
                color={layoutTitleSize === opt.value ? 'primary' : 'default'}
                onClick={() => setLayoutTitleSize(opt.value)}
              >{opt.label}</Button>
            ))}
          </div>
        </Grid>

        <Grid item xs={12}><Divider /></Grid>

        <Grid item xs={12}>
          <Typography variant="caption" display="block" style={{ marginBottom: 6, color: '#757575', fontWeight: 600 }}>
            Colores
          </Typography>
        </Grid>

        {colorField('Color de fondo', layoutBgColor, setLayoutBgColor)}
        {colorField('Color de texto', layoutTextColor, setLayoutTextColor)}

        <Grid item xs={12}><Divider /></Grid>

        <Grid item xs={12}>
          <Typography variant="caption" display="block" style={{ marginBottom: 6, color: '#757575' }}>Borde</Typography>
          <div className={classes.layoutBtnGroup}>
            {BORDER_STYLES.map((opt) => (
              <Button key={opt.value} size="small" className={classes.layoutBtn}
                variant={layoutBorderStyle === opt.value ? 'contained' : 'outlined'}
                color={layoutBorderStyle === opt.value ? 'primary' : 'default'}
                onClick={() => setLayoutBorderStyle(opt.value)}
              >{opt.label}</Button>
            ))}
          </div>
        </Grid>

        {layoutBorderStyle !== 'none' && (
          <>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" display="block" style={{ marginBottom: 4, color: '#757575' }}>Grosor</Typography>
              <div className={classes.layoutBtnGroup}>
                {BORDER_WIDTHS.map((w) => (
                  <Button key={w} size="small" className={classes.layoutBtn}
                    variant={layoutBorderWidth === w ? 'contained' : 'outlined'}
                    color={layoutBorderWidth === w ? 'primary' : 'default'}
                    onClick={() => setLayoutBorderWidth(w)}
                  >{w}px</Button>
                ))}
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" display="block" style={{ marginBottom: 4, color: '#757575' }}>Color del borde</Typography>
              <div className={classes.colorInputRow}>
                <input type="color" value={layoutBorderColor || '#e0e0e0'}
                  onChange={(e) => setLayoutBorderColor(e.target.value)}
                  style={{ width: 36, height: 36, border: '1px solid #e0e0e0', cursor: 'pointer', borderRadius: 4, padding: 2 }}
                />
                <TextField size="small" value={layoutBorderColor}
                  onChange={(e) => setLayoutBorderColor(e.target.value)}
                  placeholder="#e0e0e0"
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                  style={{ flex: 1 }}
                />
              </div>
            </Grid>
          </>
        )}

        <Grid item xs={12}><Divider /></Grid>

        <Grid item xs={12}>
          <Typography variant="caption" display="block" style={{ marginBottom: 6, color: '#757575' }}>Redondeo de esquinas</Typography>
          <div className={classes.layoutBtnGroup}>
            {BORDER_RADII.map((opt) => (
              <Button key={opt.value} size="small" className={classes.layoutBtn}
                variant={layoutBorderRadius === opt.value ? 'contained' : 'outlined'}
                color={layoutBorderRadius === opt.value ? 'primary' : 'default'}
                onClick={() => setLayoutBorderRadius(opt.value)}
              >{opt.label}</Button>
            ))}
          </div>
        </Grid>
      </Grid>
    );
  };

  // ── Render: preview de tipo (hover en el selector) ──────────────────────

  const renderSectionTypePreview = (sType) => {
    const G = [
      'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',
      'linear-gradient(135deg,#f093fb 0%,#f5576c 100%)',
      'linear-gradient(135deg,#4facfe 0%,#00f2fe 100%)',
      'linear-gradient(135deg,#43e97b 0%,#38f9d7 100%)',
    ];
    const bar  = (w = '100%', h = 7, bg = '#e0e0e0', mb = 4) => (
      <div style={{ height: h, background: bg, borderRadius: 2, width: w, marginBottom: mb }} />
    );
    const miniCard = (i, { circle, showBadge, noImg } = {}) => (
      <div style={{ flex: 1, border: '1px solid #e0e0e0', borderRadius: 6, overflow: 'hidden', background: '#fff', minWidth: 0 }}>
        {!noImg && (
          circle
            ? <div style={{ width: 36, height: 36, borderRadius: '50%', background: G[i % 4], margin: '10px auto 6px' }} />
            : <div style={{ height: 52, background: G[i % 4] }} />
        )}
        <div style={{ padding: '5px 7px' }}>
          {bar('75%', 7, '#333', 3)}
          {bar('90%', 5, '#ccc', 2)}
          {bar('60%', 5, '#ccc', 0)}
          {showBadge && (
            <div style={{ marginTop: 5, display: 'inline-block', background: '#ff5722', borderRadius: 10, padding: '2px 7px' }}>
              <div style={{ height: 5, width: 24, background: '#fff', borderRadius: 2 }} />
            </div>
          )}
        </div>
      </div>
    );

    switch (sType.value) {
      case 'hero_banner':
        return (
          <div style={{ background: G[0], borderRadius: 6, padding: '20px 12px', textAlign: 'center' }}>
            {bar('60%', 11, 'rgba(255,255,255,0.9)', 8)}
            <div style={{ margin: '0 auto 10px', width: '40%' }}>{bar('100%', 7, 'rgba(255,255,255,0.6)', 0)}</div>
            <div style={{ display: 'inline-block', background: '#ff5722', borderRadius: 4, padding: '5px 14px' }}>
              {bar(40, 7, '#fff', 0)}
            </div>
          </div>
        );
      case 'visual_cards':
        return <div style={{ display: 'flex', gap: 6 }}>{[0,1,2].map((i) => miniCard(i, { showBadge: true }))}</div>;
      case 'card':
      case 'product_cards':
      case 'service_cards':
        return <div style={{ display: 'flex', gap: 6 }}>{[0,1,2].map((i) => miniCard(i))}</div>;
      case 'team_cards':
        return <div style={{ display: 'flex', gap: 5 }}>{[0,1,2,3].map((i) => miniCard(i, { circle: true, noImg: false }))}</div>;
      case 'testimonials':
        return (
          <div style={{ display: 'flex', gap: 6 }}>
            {[0,1].map((i) => (
              <div key={i} style={{ flex: 1, border: '1px solid #e0e0e0', borderRadius: 6, padding: '8px', background: '#fff' }}>
                {bar('100%', 5, '#ccc', 3)}{bar('80%', 5, '#ccc', 3)}{bar('65%', 5, '#ccc', 8)}
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: G[i % 4], flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>{bar('60%', 6, '#555', 2)}{bar('40%', 5, '#ccc', 0)}</div>
                </div>
              </div>
            ))}
          </div>
        );
      case 'faq_accordion':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {[['#f3e5f5','#9c27b0'],['#fff','#ccc'],['#fff','#ccc']].map(([bg, c], i) => (
              <div key={i} style={{ border: `1px solid ${c}`, borderRadius: 4, padding: '7px 9px', background: bg, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {bar('65%', 7, i === 0 ? '#9c27b0' : '#555', 0)}
                <span style={{ color: '#ccc', fontSize: 14, lineHeight: 1 }}>{i === 0 ? '▲' : '▼'}</span>
              </div>
            ))}
          </div>
        );
      case 'gallery':
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 5 }}>
            {[0,1,2,3,4,5].map((i) => <div key={i} style={{ height: 40, borderRadius: 4, background: G[i%4] }} />)}
          </div>
        );
      case 'video':
        return (
          <div style={{ display: 'flex', gap: 6 }}>
            {[0,1,2].map((i) => (
              <div key={i} style={{ flex: 1, borderRadius: 6, overflow: 'hidden', background: '#1a1a2e' }}>
                <div style={{ height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: '13px solid rgba(255,255,255,0.7)' }} />
                </div>
                <div style={{ padding: '4px 6px' }}>{bar('70%', 5, '#555', 0)}</div>
              </div>
            ))}
          </div>
        );
      case 'text_block':
        return (
          <div style={{ padding: 8 }}>
            {bar('55%', 11, '#333', 10)}
            {[100,90,95,70].map((w, i) => bar(`${w}%`, 6, '#e0e0e0', 5))}
          </div>
        );
      case 'contact_form':
        return (
          <div style={{ padding: 4 }}>
            {bar('50%', 9, '#333', 10)}
            {[22,22,38].map((h, i) => (
              <div key={i} style={{ border: '1px solid #e0e0e0', borderRadius: 4, padding: '5px 7px', marginBottom: 6, height: h }} />
            ))}
            <div style={{ background: '#7b1fa2', borderRadius: 4, padding: '5px 14px', display: 'inline-block' }}>
              {bar(40, 7, '#fff', 0)}
            </div>
          </div>
        );
      case 'footer':
        return (
          <div style={{ background: '#212121', borderRadius: 6, padding: '12px 14px' }}>
            {bar('40%', 8, '#fff', 10)}
            {bar('60%', 5, 'rgba(255,255,255,0.4)', 4)}
            {bar('50%', 5, 'rgba(255,255,255,0.4)', 4)}
            {bar('45%', 5, 'rgba(255,255,255,0.4)', 0)}
          </div>
        );
      case 'search_bar':
        return (
          <div style={{ padding: 4 }}>
            {bar('45%', 9, '#333', 10)}
            <div style={{ border: '1px solid #9c27b0', borderRadius: 4, padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 13, height: 13, borderRadius: '50%', border: '2px solid #9c27b0', flexShrink: 0 }} />
              {bar('100%', 6, '#e0e0e0', 0)}
            </div>
          </div>
        );
      case 'heading_h1':
        return (
          <div style={{ padding: '12px 8px' }}>
            {bar('70%', 18, '#111', 8)}
            {bar('45%', 8, '#9e9e9e', 0)}
          </div>
        );
      case 'heading_h2':
        return (
          <div style={{ padding: '12px 8px' }}>
            {bar('60%', 13, '#222', 6)}
            {bar('40%', 7, '#9e9e9e', 0)}
          </div>
        );
      case 'heading_h3':
        return (
          <div style={{ padding: '12px 8px' }}>
            {bar('50%', 10, '#333', 0)}
          </div>
        );
      case 'paragraph_text':
        return (
          <div style={{ padding: '8px' }}>
            {[100,95,90,100,80].map((w, i) => bar(`${w}%`, 6, '#555', 5))}
          </div>
        );
      case 'text_label':
        return (
          <div style={{ padding: '14px 8px' }}>
            <div style={{ display: 'inline-block', background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: 4, padding: '4px 10px' }}>
              {bar(60, 7, '#e65100', 0)}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // ── Render: contenido WYSIWYG por tipo de sección ───────────────────────

  const TITLE_SIZE_VARIANT = { sm: 'h6', md: 'h5', lg: 'h4', xl: 'h3' };
  const TITLE_SIZE_PX      = { sm: 18,  md: 24,   lg: 32,   xl: 42 };
  const MIN_HEIGHT_PX      = { auto: undefined, sm: 200, md: 350, lg: 500, screen: '100vh' };
  const CONTAINER_MAX_W    = { full: '100%', wide: 1200, medium: 900, narrow: 600 };

  const renderSectionPreviewContent = (section) => {
    const layout   = section.layout || {};
    const tc       = layout.textColor || '#333';
    const fields   = section.fields || [];
    const hasImg   = fields.some((f) => f.type === 'image');
    const imgPh    = 'linear-gradient(135deg,#e0e0e0 0%,#bdbdbd 100%)';
    const tSize    = layout.titleSize || 'md';
    const align    = layout.alignment || 'center';
    const titleSt  = { color: tc, fontWeight: 700, marginBottom: 8, fontSize: TITLE_SIZE_PX[tSize] };
    const bodySt   = { color: tc, opacity: 0.7 };

    switch (section.type) {
      case 'hero_banner':
        return (
          <div style={{ textAlign: align, padding: '32px 0' }}>
            <Typography variant={TITLE_SIZE_VARIANT[tSize]} style={{ ...titleSt, marginBottom: 12 }}>{section.label}</Typography>
            <Typography variant="body1" style={{ ...bodySt, marginBottom: 24 }}>
              Subtítulo descriptivo de esta sección hero
            </Typography>
            <Button variant="contained" color="primary" size="large">
              {fields.find((f) => f.type === 'url')?.label || 'Ver más'}
            </Button>
          </div>
        );

      case 'footer':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Typography variant="h6" style={titleSt}>{section.label}</Typography>
              {fields.slice(0, 3).map((f) => (
                <Typography key={f.key} variant="body2" style={{ ...bodySt, marginBottom: 4 }}>{f.label}</Typography>
              ))}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="h6" style={titleSt}>Navegación</Typography>
              {['Inicio', 'Servicios', 'Contacto'].map((t) => (
                <Typography key={t} variant="body2" style={{ ...bodySt, marginBottom: 4 }}>{t}</Typography>
              ))}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" style={bodySt}>© 2024 Tu Empresa. Todos los derechos reservados.</Typography>
            </Grid>
          </Grid>
        );

      case 'contact_form':
        return (
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={8}>
              <Typography variant={TITLE_SIZE_VARIANT[tSize]} align={align} style={{ ...titleSt, marginBottom: 24 }}>{section.label}</Typography>
              {fields.slice(0, 3).map((f) => (
                <TextField key={f.key} label={f.label} fullWidth variant="outlined" size="small"
                  style={{ marginBottom: 12 }} InputProps={{ readOnly: true }} />
              ))}
              <Button variant="contained" color="primary" fullWidth style={{ marginTop: 4 }}>Enviar</Button>
            </Grid>
          </Grid>
        );

      case 'gallery':
        return (
          <div>
            <Typography variant={TITLE_SIZE_VARIANT[tSize]} align={align} style={{ ...titleSt, marginBottom: 20 }}>{section.label}</Typography>
            <Grid container spacing={2}>
              {[0,1,2,3,4,5].map((i) => (
                <Grid key={i} item xs={4}>
                  <div style={{ height: 120, borderRadius: 8, background: imgPh }} />
                </Grid>
              ))}
            </Grid>
          </div>
        );

      case 'text_block':
      case 'paragraph_text':
        return (
          <div style={{ textAlign: align }}>
            <Typography variant={TITLE_SIZE_VARIANT[tSize]} gutterBottom style={titleSt}>{section.label}</Typography>
            {[0,1,2].map((i) => (
              <Typography key={i} variant="body1" paragraph style={bodySt}>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore.
              </Typography>
            ))}
          </div>
        );

      case 'heading_h1':
        return <Typography variant="h2" align={align} style={{ ...titleSt, fontSize: TITLE_SIZE_PX[tSize] * 2, color: tc }}>{section.label}</Typography>;
      case 'heading_h2':
        return <Typography variant="h3" align={align} style={{ ...titleSt, fontSize: TITLE_SIZE_PX[tSize] * 1.5, color: tc }}>{section.label}</Typography>;
      case 'heading_h3':
        return <Typography variant="h4" align={align} style={{ ...titleSt, fontSize: TITLE_SIZE_PX[tSize] * 1.2, color: tc }}>{section.label}</Typography>;
      case 'text_label':
        return <Typography variant="body1" align={align} style={{ ...bodySt }}>{section.label}</Typography>;

      case 'search_bar':
        return (
          <div>
            <Typography variant={TITLE_SIZE_VARIANT[tSize]} align={align} style={{ ...titleSt, marginBottom: 16 }}>{section.label}</Typography>
            <TextField placeholder="Buscar..." fullWidth variant="outlined"
              InputProps={{ startAdornment: <Icons.Search style={{ color: '#9c27b0', marginRight: 8 }} />, readOnly: true }}
            />
          </div>
        );

      case 'faq_accordion':
        return (
          <div>
            <Typography variant={TITLE_SIZE_VARIANT[tSize]} align={align} style={{ ...titleSt, marginBottom: 20 }}>{section.label}</Typography>
            {fields.slice(0, 4).map((f) => (
              <div key={f.key} style={{
                border: '1px solid #e0e0e0', borderRadius: 4, marginBottom: 8,
                padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <Typography variant="body1" style={{ color: tc }}>{f.label}</Typography>
                <Icons.ExpandMore style={{ color: '#9e9e9e' }} />
              </div>
            ))}
          </div>
        );

      case 'video':
        return (
          <div>
            <Typography variant={TITLE_SIZE_VARIANT[tSize]} align={align} style={{ ...titleSt, marginBottom: 20 }}>{section.label}</Typography>
            <Grid container spacing={2}>
              {[0,1,2].map((i) => (
                <Grid key={i} item xs={4}>
                  <div style={{ borderRadius: 8, overflow: 'hidden', background: '#1a1a2e' }}>
                    <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: 0, height: 0, borderTop: '14px solid transparent', borderBottom: '14px solid transparent', borderLeft: '22px solid rgba(255,255,255,0.7)' }} />
                    </div>
                    <div style={{ padding: '8px 10px' }}>
                      <Typography variant="caption" style={{ color: '#aaa' }}>Título del video {i + 1}</Typography>
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
          </div>
        );

      default: {
        const display  = layout.display || 'grid_3';
        const isList   = display === 'list';
        const colCount = display === 'grid_4' ? 4 : display === 'grid_2' ? 2 : isList ? 1 : 3;
        const cardCols = fields.filter((f) => f.type !== 'image');
        const repeatCount = isList ? 3 : colCount;
        return (
          <div>
            <Typography variant={TITLE_SIZE_VARIANT[tSize]} align={align} style={{ ...titleSt, marginBottom: 24 }}>{section.label}</Typography>
            <Grid container spacing={2}>
              {Array.from({ length: repeatCount }).map((_, i) => (
                <Grid key={i} item xs={12} sm={isList ? 12 : Math.floor(12 / colCount)}>
                  <Paper elevation={1} style={{
                    padding: 16,
                    display: 'flex',
                    flexDirection: isList ? 'row' : 'column',
                    gap: 12,
                    alignItems: isList ? 'center' : 'stretch',
                  }}>
                    {hasImg && (
                      <div style={{
                        height: isList ? 64 : 140,
                        width: isList ? 100 : '100%',
                        borderRadius: 6,
                        background: imgPh,
                        flexShrink: 0,
                      }} />
                    )}
                    <div style={{ flex: 1 }}>
                      {cardCols.slice(0, 3).map((f) => (
                        <Typography key={f.key} variant={f.type === 'richtext' ? 'body2' : 'body1'}
                          style={{ color: tc, marginBottom: 4, opacity: f.type === 'richtext' ? 0.7 : 1 }}
                        >
                          {f.label}
                        </Typography>
                      ))}
                    </div>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </div>
        );
      }
    }
  };

  const renderPreviewSection = (section) => {
    const layout     = section.layout || {};
    const borderCSS  = getSectionBorderCSS(layout);
    const cw         = layout.containerWidth || 'wide';
    const maxW       = CONTAINER_MAX_W[cw] ?? 1200;
    const minH       = MIN_HEIGHT_PX[layout.minHeight || 'auto'];
    const align      = layout.alignment || 'center';
    const outerStyle = {
      backgroundColor: layout.backgroundColor || 'transparent',
      paddingTop:      PADDING_PX[layout.paddingTop]    ?? 40,
      paddingBottom:   PADDING_PX[layout.paddingBottom] ?? 40,
      minHeight:       minH,
      display:         minH ? 'flex' : 'block',
      alignItems:      minH ? 'center' : undefined,
      ...borderCSS,
      borderRadius:    layout.borderRadius ? `${layout.borderRadius}px` : 0,
    };
    const innerStyle = {
      width:    '100%',
      maxWidth: maxW,
      margin:   '0 auto',
      padding:  '0 24px',
      textAlign: align,
      color:    layout.textColor || 'inherit',
    };
    const containerStyle = {
      position: 'relative',
      overflow: 'hidden',
      ...outerStyle,
    };

    return (
      <div key={section.id} className={classes.previewSection}>
        <div style={containerStyle}>
          <div style={innerStyle}>
            {renderSectionPreviewContent(section)}
          </div>
        </div>
        <div className={classes.previewOverlay}>
          <Tooltip title="Editar sección" enterDelay={200}>
            <IconButton size="small" onClick={() => handleOpenEditSection(section)}
              style={{ backgroundColor: 'rgba(255,255,255,0.92)', padding: 4 }}>
              <Icons.Edit style={{ fontSize: 14, color: '#1976d2' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar sección" enterDelay={200}>
            <IconButton size="small" onClick={() => handleDeleteSection(section.id)}
              style={{ backgroundColor: 'rgba(255,255,255,0.92)', padding: 4 }}>
              <Icons.Delete style={{ fontSize: 14, color: '#f44336' }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  };

  const renderPreviewCanvas = () => {
    if (sections.length === 0) {
      return (
        <div className={classes.previewCanvas} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className={classes.emptyCanvas}>
            <Icons.Visibility style={{ fontSize: 64, opacity: 0.25 }} />
            <Typography variant="body1" style={{ marginTop: 8 }}>No hay secciones para previsualizar.</Typography>
          </div>
        </div>
      );
    }

    const sorted = [...sections].sort((a, b) => {
      const ay = a.layout?.y ?? 0, by = b.layout?.y ?? 0;
      if (ay !== by) return ay - by;
      return (a.layout?.x ?? 0) - (b.layout?.x ?? 0);
    });

    const pageBg = pageStyle.backgroundColor || '#ffffff';
    const pageBgStyle = {
      backgroundColor: pageBg,
      backgroundImage: pageStyle.backgroundImage
        ? `url(${pageStyle.backgroundImage})`
        : undefined,
      backgroundSize:     'cover',
      backgroundPosition: 'center',
      minHeight: 400,
    };

    return (
      <div className={classes.previewCanvas}>
        <div style={pageBgStyle}>
          {sorted.map((section) => renderPreviewSection(section))}
        </div>
      </div>
    );
  };

  // ── Render: canvas con grid ──────────────────────────────────────────────

  const renderCanvas = () => {
    if (sections.length === 0) {
      return (
        <div className={classes.canvasWrap}>
          <div className={classes.emptyCanvas}>
            <Icons.ViewModule style={{ fontSize: 64, opacity: 0.25 }} />
            <Typography variant="body1" style={{ marginTop: 8 }}>Esta página no tiene secciones.</Typography>
            <Typography variant="body2">Presiona "Agregar sección" para comenzar.</Typography>
          </div>
        </div>
      );
    }

    const gridItems = sections.map((s) => ({
      i:    s.id,
      x:    s.layout?.x ?? 0,
      y:    s.layout?.y ?? 0,
      w:    s.layout?.w ?? 12,
      h:    s.layout?.h ?? getDefaultH(s.type),
      minW: 2,
      maxW: 12,
      minH: 1,
    }));

    return (
      <div className={classes.canvasWrap}>
        <RGL
          layout={gridItems}
          cols={12}
          rowHeight={60}
          isDraggable
          isResizable
          resizeHandles={['se']}
          draggableHandle=".drag-handle"
          onDragStop={handleLayoutStop}
          onResizeStop={handleLayoutStop}
          margin={[8, 8]}
          containerPadding={[4, 4]}
          compactType={null}
          preventCollision={false}
        >
          {sections.map((section) => (
            <div key={section.id}>
              {renderSectionCard(section)}
            </div>
          ))}
        </RGL>
      </div>
    );
  };

  const renderContainerMiniCards = (display) => {
    const count = display === 'grid_4' ? 4 : display === 'grid_2' ? 2 : display === 'list' ? 2 : 3;
    return (
      <div style={{ display: 'flex', gap: 3, marginTop: 5, flexDirection: display === 'list' ? 'column' : 'row' }}>
        {Array.from({ length: Math.min(count, 4) }).map((_, i) => (
          <div
            key={i}
            style={{
              flex: 1, height: display === 'list' ? 10 : 22,
              background: i === 0 ? '#e1bee7' : '#f3e5f5',
              border: '1px dashed #ce93d8',
              borderRadius: 3,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {i === 0 && (
              <Typography style={{ fontSize: 7, color: '#6a1b9a', lineHeight: 1 }}>ejemplo</Typography>
            )}
          </div>
        ))}
      </div>
    );
  };

  const TYPO_STYLES = {
    heading_h1:     { fontSize: 20, fontWeight: 800, color: '#111', label: 'H1' },
    heading_h2:     { fontSize: 15, fontWeight: 700, color: '#222', label: 'H2' },
    heading_h3:     { fontSize: 12, fontWeight: 600, color: '#333', label: 'H3' },
    paragraph_text: { fontSize: 11, fontWeight: 400, color: '#555', label: 'P'  },
    text_label:     { fontSize: 10, fontWeight: 400, color: '#e65100', label: 'Label' },
  };

  const renderSectionCard = (section) => {
    const sType      = getSectionType(section.type);
    const layout     = section.layout || {};
    const isContainer  = sType?.isContainer;
    const isTypography = sType?.isTypography;
    const iconColor = isTypography ? '#e65100' : isContainer ? '#2e7d32' : '#9c27b0';

    return (
      <div className={classes.sectionCard} style={ isTypography ? { borderColor: '#ffe0b2' } : {} }>
        {/* Drag handle — barra superior */}
        <div className={`drag-handle ${classes.dragBar}`}
          style={ isTypography ? { background: 'linear-gradient(180deg,#fff8f0 0%,#fff3e0 100%)' } : {} }
        >
          <Icons.DragIndicator className={classes.dragIcon} />
          {sType && <Icon style={{ fontSize: 15, color: iconColor, flexShrink: 0 }}>{sType.icon}</Icon>}
          <Typography className={classes.cardTitle} title={section.label}>{section.label}</Typography>
          <Tooltip title="Editar" enterDelay={400}>
            <IconButton
              size="small"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); handleOpenEditSection(section); }}
              style={{ color: '#1976d2', padding: 2 }}
            >
              <Icons.Edit style={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar" enterDelay={400}>
            <IconButton
              size="small"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); handleDeleteSection(section.id); }}
              style={{ color: '#f44336', padding: 2 }}
            >
              <Icons.Delete style={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Cuerpo del card */}
        <div className={classes.cardBody}>
          {isTypography ? (
            <>
              <div className={classes.chipRow}>
                <Chip label={TYPO_STYLES[section.type]?.label || 'Texto'} size="small" className={classes.typographyChip} />
              </div>
              <Typography style={{
                ...TYPO_STYLES[section.type],
                marginTop: 4,
                overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
              }}>
                {section.label}
              </Typography>
            </>
          ) : isContainer ? (
            <>
              <div className={classes.chipRow}>
                <Chip label="Contenedor" size="small" className={classes.containerChip} />
                {layout.display && layout.display !== 'single' && (
                  <Chip label={getDisplayLabel(layout.display)} size="small" className={classes.displayChip} />
                )}
              </div>
              {section.canBelist !== false && renderContainerMiniCards(layout.display)}
            </>
          ) : (
            <>
              <div className={classes.chipRow}>
                <Chip
                  label={section.isList ? 'Lista' : 'Único'}
                  size="small"
                  className={section.isList ? classes.listChip : classes.uniqueChip}
                />
                {section.isList && layout.display && (
                  <Chip label={getDisplayLabel(layout.display)} size="small" className={classes.displayChip} />
                )}
              </div>
              <div>
                {section.fields.map((field) => (
                  <Chip
                    key={field.key}
                    label={`{${field.key}}`}
                    size="small"
                    className={classes.fieldChip}
                    title={`${field.label} (${field.type})${field.required ? ' — requerido' : ''}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ── Render: paso 1 — Selector de tipo ────────────────────────────────────

  const renderTypeSelector = () => {
    const regularTypes    = SECTION_TYPES.filter((st) => !st.isContainer && !st.isTypography && !st.isDetailTemplate);
    const containerTypes  = SECTION_TYPES.filter((st) => st.isContainer);
    const typographyTypes = SECTION_TYPES.filter((st) => st.isTypography);
    const detailTypes     = SECTION_TYPES.filter((st) => st.isDetailTemplate);
    const typeIconColor   = (sType) => sType.isDetailTemplate ? '#c62828' : sType.isTypography ? '#e65100' : sType.isContainer ? '#2e7d32' : '#9c27b0';
    const renderTypeCard  = (sType) => (
      <div
        key={sType.value}
        className={`${classes.typeCard} ${selectedType?.value === sType.value ? classes.typeCardSelected : ''}`}
        onClick={() => handleSelectType(sType)}
        onMouseEnter={() => setHoverType(sType)}
        onMouseLeave={() => setHoverType(null)}
        style={ sType.isTypography && selectedType?.value !== sType.value
          ? { borderColor: '#ffe0b2' }
          : {} }
      >
        <Icon className={classes.typeCardIcon} style={{ color: typeIconColor(sType) }}>
          {sType.icon}
        </Icon>
        <Typography variant="caption" display="block" style={{ fontWeight: 600 }}>{sType.label}</Typography>
        <Typography variant="caption" display="block" style={{ color: '#757575', fontSize: 10 }}>{sType.description}</Typography>
      </div>
    );
    return (
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        {/* Panel izquierdo: tipos */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography className={classes.stepTitle}>Selecciona el tipo de sección</Typography>

          <Typography variant="subtitle2" style={{ color: '#555', marginBottom: 6 }}>Secciones</Typography>
          <div className={classes.typeGrid}>
            {regularTypes.map(renderTypeCard)}
          </div>

          <Divider style={{ margin: '14px 0 10px' }} />
          <Typography variant="subtitle2" style={{ color: '#2e7d32', marginBottom: 6 }}>
            Layouts y Contenedores
          </Typography>
          <Typography variant="caption" display="block" style={{ color: '#757575', marginBottom: 8 }}>
            Campos prediseñados. Elementos dinámicos en el gestor de contenido.
          </Typography>
          <div className={classes.typeGrid}>
            {containerTypes.map(renderTypeCard)}
          </div>

          <Divider style={{ margin: '14px 0 10px' }} />
          <Typography variant="subtitle2" style={{ color: '#e65100', marginBottom: 6 }}>
            Tipografía
          </Typography>
          <Typography variant="caption" display="block" style={{ color: '#757575', marginBottom: 8 }}>
            Títulos y textos posicionables: H1, H2, H3, párrafo, etiqueta.
          </Typography>
          <div className={classes.typeGrid}>
            {typographyTypes.map(renderTypeCard)}
          </div>

          <Divider style={{ margin: '14px 0 10px' }} />
          <Typography variant="subtitle2" style={{ color: '#c62828', marginBottom: 6 }}>
            Páginas de Detalle
          </Typography>
          <Typography variant="caption" display="block" style={{ color: '#757575', marginBottom: 8 }}>
            Plantillas prediseñadas para mostrar el detalle completo de un elemento.
          </Typography>
          <div className={classes.typeGrid}>
            {detailTypes.map(renderTypeCard)}
          </div>

          {sectionDialogError && <Alert severity="error" style={{ marginTop: 12 }}>{sectionDialogError}</Alert>}
        </div>

        {/* Panel derecho: preview en hover */}
        <div style={{ width: 196, flexShrink: 0, position: 'sticky', top: 0 }}>
          <Typography variant="caption" style={{ color: '#9e9e9e', fontWeight: 600, display: 'block', marginBottom: 8 }}>
            {hoverType ? hoverType.label : 'Vista previa'}
          </Typography>
          {hoverType ? (
            <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 12, background: '#fafafa', minHeight: 100 }}>
              {renderSectionTypePreview(hoverType)}
              {hoverType.description && (
                <Typography variant="caption" style={{ color: '#9e9e9e', display: 'block', marginTop: 10, lineHeight: 1.4 }}>
                  {hoverType.description}
                </Typography>
              )}
            </div>
          ) : (
            <div style={{
              border: '1px dashed #e0e0e0', borderRadius: 8, padding: '24px 12px',
              textAlign: 'center', color: '#bdbdbd', minHeight: 100,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icons.Visibility style={{ fontSize: 28, opacity: 0.3 }} />
              <Typography variant="caption" display="block" style={{ marginTop: 8, lineHeight: 1.4 }}>
                Pasa el cursor sobre un tipo para ver un ejemplo
              </Typography>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Render: paso 2 — Configuración ───────────────────────────────────────

  const renderVisualizationOptions = (showDisplaySelector) => (
    <>
      <Divider style={{ margin: '20px 0 12px' }} />
      <Typography variant="subtitle2" gutterBottom style={{ color: '#555' }}>Visualización</Typography>
      <Grid container spacing={2}>
        {showDisplaySelector && (
          <Grid item xs={12}>
            <Typography variant="caption" display="block" style={{ marginBottom: 4, color: '#757575' }}>
              Presentación de los elementos
            </Typography>
            <div className={classes.layoutBtnGroup}>
              {DISPLAY_OPTIONS.map((opt) => (
                <Button key={opt.value} size="small" className={classes.layoutBtn}
                  variant={layoutDisplay === opt.value ? 'contained' : 'outlined'}
                  color={layoutDisplay === opt.value ? 'primary' : 'default'}
                  onClick={() => setLayoutDisplay(opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" display="block" style={{ marginBottom: 4, color: '#757575' }}>Espacio superior</Typography>
          <div className={classes.layoutBtnGroup}>
            {PADDING_OPTIONS.map((opt) => (
              <Button key={opt.value} size="small" className={classes.layoutBtn}
                variant={layoutPaddingTop === opt.value ? 'contained' : 'outlined'}
                color={layoutPaddingTop === opt.value ? 'primary' : 'default'}
                onClick={() => setLayoutPaddingTop(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" display="block" style={{ marginBottom: 4, color: '#757575' }}>Espacio inferior</Typography>
          <div className={classes.layoutBtnGroup}>
            {PADDING_OPTIONS.map((opt) => (
              <Button key={opt.value} size="small" className={classes.layoutBtn}
                variant={layoutPaddingBottom === opt.value ? 'contained' : 'outlined'}
                color={layoutPaddingBottom === opt.value ? 'primary' : 'default'}
                onClick={() => setLayoutPaddingBottom(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </Grid>
      </Grid>
    </>
  );

  const renderContainerConfig = () => (
    <div>
      <Typography className={classes.stepTitle}>Configuración: {selectedType?.label}</Typography>
      <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)} indicatorColor="primary" textColor="primary"
        style={{ borderBottom: '1px solid #e0e0e0', marginBottom: 16 }}>
        <Tab label="Contenido" />
        <Tab label="Apariencia" />
      </Tabs>
      {dialogTab === 0 && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Nombre de la sección" fullWidth autoFocus
                value={sectionLabel}
                onChange={(e) => setSectionLabel(e.target.value)}
                helperText='Ej: "Nuestros Servicios", "Equipo"'
              />
            </Grid>
            <Grid item xs={12}>
              <div style={{ background: '#f1f8e9', borderRadius: 6, padding: '10px 14px', border: '1px solid #a5d6a7' }}>
                <Typography variant="caption" style={{ color: '#2e7d32', fontWeight: 600 }}>
                  Campos prediseñados
                </Typography>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                  {sectionFields.map((f) => (
                    <Chip
                      key={f.key}
                      label={`${f.label}${f.required ? ' *' : ''}`}
                      size="small"
                      style={{ fontSize: 10, backgroundColor: '#dcedc8', color: '#33691e', height: 18 }}
                    />
                  ))}
                </div>
                <Typography variant="caption" style={{ color: '#757575', display: 'block', marginTop: 6 }}>
                  Los elementos se agregan dinámicamente en el gestor de contenido.
                </Typography>
              </div>
            </Grid>
            {selectedType?.canBelist && !selectedType?.isDetailTemplate && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Página de detalle (opcional)</InputLabel>
                  <Select value={sectionDetailPageId} onChange={(e) => setSectionDetailPageId(e.target.value)}>
                    <MenuItem value=""><em>Sin página de detalle</em></MenuItem>
                    {allPages.filter((p) => p.pageType === 'guided' && p.pageId !== currentPageId).map((p) => (
                      <MenuItem key={p.pageId} value={p.pageId}>
                        {p.title || p.pageId}{p.slug ? ` (/${p.slug})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {sectionDetailPageId && (
                  <Typography variant="caption" style={{ color: '#7b1fa2', display: 'block', marginTop: 4 }}>
                    El botón del card abrirá: /{allPages.find((p) => p.pageId === sectionDetailPageId)?.slug || ''}/{'{slug}'}
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>
          {renderVisualizationOptions(selectedType?.canBelist)}
        </>
      )}
      {dialogTab === 1 && renderAppearanceOptions()}
      {sectionDialogError && <Alert severity="error" style={{ marginTop: 12 }}>{sectionDialogError}</Alert>}
    </div>
  );

  const renderDetailBlockConfig = () => (
    <div>
      <Typography className={classes.stepTitle}>Configuración: {selectedType?.label}</Typography>
      <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)} indicatorColor="primary" textColor="primary"
        style={{ borderBottom: '1px solid #e0e0e0', marginBottom: 16 }}>
        <Tab label="Plantilla" />
        <Tab label="Apariencia" />
      </Tabs>
      {dialogTab === 0 && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Nombre de la sección" fullWidth autoFocus
                value={sectionLabel}
                onChange={(e) => setSectionLabel(e.target.value)}
                helperText='Ej: "Detalle del servicio"'
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Elige una plantilla visual</Typography>
              <div style={{ background: '#fef3f3', borderRadius: 6, padding: '8px 12px', border: '1px solid #ffcdd2', marginBottom: 14 }}>
                <Typography variant="caption" style={{ color: '#c62828' }}>
                  La plantilla define cómo se visualizará el contenido del ítem seleccionado en el sitio publicado.
                </Typography>
              </div>
              <Grid container spacing={2}>
                {DETAIL_TEMPLATES.map((tmpl) => (
                  <Grid item xs={12} sm={6} key={tmpl.value}>
                    <div
                      onClick={() => { setSectionDetailTemplate(tmpl.value); setSectionFields((DETAIL_TEMPLATE_FIELDS[tmpl.value] || []).map((f) => ({ ...f }))); }}
                      style={{
                        cursor: 'pointer',
                        border: sectionDetailTemplate === tmpl.value
                          ? `2px solid ${tmpl.color}`
                          : '2px solid #e0e0e0',
                        borderRadius: 8,
                        padding: 14,
                        backgroundColor: sectionDetailTemplate === tmpl.value ? `${tmpl.color}12` : '#fff',
                        transition: 'all 0.15s',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 12,
                      }}
                    >
                      <div style={{
                        width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                        backgroundColor: tmpl.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon style={{ color: '#fff', fontSize: 20 }}>{tmpl.icon}</Icon>
                      </div>
                      <div style={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 600, color: sectionDetailTemplate === tmpl.value ? tmpl.color : '#333' }}>
                          {tmpl.label}
                        </Typography>
                        <Typography variant="caption" style={{ color: '#757575', display: 'block', marginBottom: 6 }}>
                          {tmpl.description}
                        </Typography>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {tmpl.preview.map((f) => (
                            <Chip key={f} label={f} size="small" style={{
                              fontSize: 10, height: 18,
                              backgroundColor: sectionDetailTemplate === tmpl.value ? `${tmpl.color}22` : '#f5f5f5',
                              color: sectionDetailTemplate === tmpl.value ? tmpl.color : '#757575',
                            }} />
                          ))}
                        </div>
                      </div>
                      {sectionDetailTemplate === tmpl.value && (
                        <Icons.CheckCircle style={{ color: tmpl.color, fontSize: 20, flexShrink: 0, marginTop: 2 }} />
                      )}
                    </div>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
      {dialogTab === 1 && renderAppearanceOptions()}
      {sectionDialogError && <Alert severity="error" style={{ marginTop: 12 }}>{sectionDialogError}</Alert>}
    </div>
  );

  const renderSectionConfig = () => {
    if (selectedType?.isDetailTemplate) return renderDetailBlockConfig();
    if (selectedType?.isContainer || selectedType?.isTypography) return renderContainerConfig();
    return (
      <div>
        <Typography className={classes.stepTitle}>Configuración: {selectedType?.label}</Typography>
        <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)} indicatorColor="primary" textColor="primary"
          style={{ borderBottom: '1px solid #e0e0e0', marginBottom: 16 }}>
          <Tab label="Contenido" />
          <Tab label="Apariencia" />
        </Tabs>

        {dialogTab === 0 && (
          <>
            <Grid container spacing={2}>

              <Grid item xs={12}>
                <TextField
                  label="Nombre de la sección" fullWidth autoFocus
                  value={sectionLabel}
                  onChange={(e) => setSectionLabel(e.target.value)}
                  helperText='Ej: "Nuestros Servicios", "Videos destacados"'
                />
              </Grid>

              {selectedType?.canBelist && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch checked={sectionIsList} color="primary"
                        onChange={(e) => { setSectionIsList(e.target.checked); setLayoutDisplay(getDefaultDisplay(selectedType.value, e.target.checked)); }}
                      />
                    }
                    label={
                      <span>
                        <b>{sectionIsList ? 'Lista de elementos' : 'Elemento único'}</b>
                        <Typography variant="caption" display="block" style={{ color: '#757575' }}>
                          {sectionIsList
                            ? `Se podrán agregar múltiples ${selectedType.listLabel?.toLowerCase() || 'elementos'} en el gestor de contenido.`
                            : 'Solo habrá un elemento de este tipo.'}
                        </Typography>
                      </span>
                    }
                  />
                </Grid>
              )}

              {(sectionIsList || selectedType?.canBelist) && !selectedType?.isDetailTemplate && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Página de detalle (opcional)</InputLabel>
                    <Select value={sectionDetailPageId} onChange={(e) => setSectionDetailPageId(e.target.value)}>
                      <MenuItem value=""><em>Sin página de detalle</em></MenuItem>
                      {allPages.filter((p) => p.pageType === 'guided' && p.pageId !== currentPageId).map((p) => (
                        <MenuItem key={p.pageId} value={p.pageId}>
                          {p.title || p.pageId}{p.slug ? ` (/${p.slug})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {sectionDetailPageId && (
                    <Typography variant="caption" style={{ color: '#7b1fa2', display: 'block', marginTop: 4 }}>
                      El botón del card abrirá: /{allPages.find((p) => p.pageId === sectionDetailPageId)?.slug || ''}/{'{slug}'}
                    </Typography>
                  )}
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Campos</Typography>
                <Table size="small" style={{ marginBottom: 8 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Clave</TableCell>
                      <TableCell>Etiqueta</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell align="center">Req.</TableCell>
                      <TableCell align="center">Elim.</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sectionFields.map((field) => (
                      <TableRow key={field.key}>
                        <TableCell>
                          <code style={{ color: '#1565c0', backgroundColor: '#e3f2fd', padding: '2px 4px', borderRadius: 3 }}>{field.key}</code>
                        </TableCell>
                        <TableCell>{field.label}</TableCell>
                        <TableCell>
                          <Typography variant="caption">{FIELD_TYPES.find((ft) => ft.value === field.type)?.label || field.type}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox size="small" checked={!!field.required} onChange={() => handleToggleRequired(field.key)} />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleDeleteField(field.key)} style={{ color: '#f44336' }}>
                            <Icons.Close fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {sectionFields.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" style={{ color: '#9e9e9e' }}>Sin campos</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {!showAddField ? (
                  <Button size="small" startIcon={<Icons.Add />} onClick={() => setShowAddField(true)} style={{ color: '#7b1fa2' }}>
                    Agregar campo
                  </Button>
                ) : (
                  <div className={classes.addFieldRow}>
                    <Grid container spacing={1} alignItems="flex-end">
                      <Grid item xs={12} sm={4}>
                        <TextField label="Etiqueta" size="small" fullWidth autoFocus value={newFieldLabel}
                          onChange={(e) => { setNewFieldLabel(e.target.value); setNewFieldError(''); }}
                          helperText={newFieldLabel ? `clave: ${labelToKey(newFieldLabel)}` : ''}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Tipo</InputLabel>
                          <Select value={newFieldType} onChange={(e) => setNewFieldType(e.target.value)}>
                            {FIELD_TYPES.map((ft) => <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <FormControlLabel
                          control={<Checkbox size="small" checked={newFieldRequired} onChange={(e) => setNewFieldRequired(e.target.checked)} />}
                          label={<Typography variant="caption">Requerido</Typography>}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3} style={{ display: 'flex', gap: 4 }}>
                        <Button size="small" variant="contained" color="primary" onClick={handleAddField}>Agregar</Button>
                        <Button size="small" onClick={resetNewField}>Cancelar</Button>
                      </Grid>
                    </Grid>
                    {newFieldError && <Alert severity="error" style={{ marginTop: 8 }}>{newFieldError}</Alert>}
                  </div>
                )}
              </Grid>
            </Grid>
            {renderVisualizationOptions(sectionIsList)}
          </>
        )}

        {dialogTab === 1 && renderAppearanceOptions()}
        {sectionDialogError && <Alert severity="error" style={{ marginTop: 12 }}>{sectionDialogError}</Alert>}
      </div>
    );
  };

  // ── Render principal ─────────────────────────────────────────────────────

  const hasPages = pages.length > 0;

  return (
    <Grid container spacing={2} className={classes.root} justifyContent="center">
      <Grid item xs={12} sm={12} md={11} lg={10}>
        <Paper>
          {/* Toolbar */}
          <div className={classes.toolbar}>
            <Tooltip title="Volver a Diseños" enterDelay={400}>
              <IconButton onClick={() => history.push('/designs')}>
                <Icons.ArrowBack />
              </IconButton>
            </Tooltip>
            <div className={classes.toolbarTitle}>
              <Typography variant="h6" style={{ lineHeight: 1.1 }}>Diseño Guiado</Typography>
              <Typography variant="caption" style={{ color: '#757575' }}>{designName}</Typography>
            </div>
            {hasPages && (
              <>
                <Button startIcon={<Icons.Add />}
                  onClick={() => { setNewPageTitle(''); setNewPageError(''); setOpenNewPage(true); }}
                  style={{ color: '#7b1fa2' }}
                >
                  Nueva página
                </Button>
                <Button variant="contained" color="primary"
                  startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Icons.Save />}
                  onClick={handleSave} disabled={isSaving}
                >
                  Guardar
                </Button>
              </>
            )}
          </div>

          {/* Cuerpo */}
          {isLoadingInfo || isListing ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <CircularProgress />
              <Typography variant="body2" style={{ marginTop: 12, color: '#9e9e9e' }}>Cargando diseño...</Typography>
            </div>
          ) : !hasPages ? (
            <div className={classes.noPages}>
              <Icons.Web style={{ fontSize: 72, color: '#e0e0e0' }} />
              <Typography variant="h6" gutterBottom style={{ marginTop: 12, color: '#616161' }}>
                No tienes páginas en este diseño guiado
              </Typography>
              <Typography variant="body2" style={{ color: '#9e9e9e', marginBottom: 24 }}>
                Crea tu primera página para comenzar a definir las secciones.
              </Typography>
              <Button variant="contained" color="primary" size="large" startIcon={<Icons.AddCircleOutline />}
                onClick={() => { setNewPageTitle(''); setNewPageError(''); setOpenNewPage(true); }}
              >
                Crear primera página
              </Button>
            </div>
          ) : (
            <div style={{ padding: '0 16px 24px' }}>
              {pages.length > 1 && (
                <Tabs value={tabIndex} onChange={handleTabChange} className={classes.pageTabsBar}
                  indicatorColor="primary" textColor="primary" variant="scrollable"
                >
                  {pages.map((p) => <Tab key={p.pageId} label={p.title} />)}
                </Tabs>
              )}

              <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0 10px', gap: 8 }}>
                <Typography variant="subtitle1" style={{ flexGrow: 1, fontWeight: 600 }}>
                  {currentPageTitle}
                  {isLoading && <CircularProgress size={14} style={{ marginLeft: 8 }} />}
                </Typography>
                {!previewMode && (
                  <Typography variant="caption" style={{ color: '#9e9e9e' }}>
                    Arrastra para mover · Esquina inferior derecha para redimensionar
                  </Typography>
                )}
                <Tooltip title="Eliminar esta página" enterDelay={300}>
                  <Button size="small"
                    startIcon={<Icons.DeleteOutline style={{ fontSize: 16 }} />}
                    onClick={() => setOpenDeletePage(true)}
                    style={{ borderColor: '#ef9a9a', color: '#c62828', minWidth: 0, padding: '4px 10px', border: '1px solid', borderRadius: 20 }}
                  >
                    Eliminar
                  </Button>
                </Tooltip>
                <Tooltip title="Fondo de página" enterDelay={300}>
                  <Button size="small"
                    startIcon={<Icons.FormatColorFill style={{ fontSize: 16 }} />}
                    onClick={() => setOpenPageStyle((v) => !v)}
                    style={{
                      borderColor: openPageStyle ? '#9c27b0' : '#e0e0e0',
                      color:       openPageStyle ? '#9c27b0' : '#757575',
                      minWidth: 0, padding: '4px 10px',
                      border: '1px solid',
                      borderRadius: 20,
                      ...(pageStyle.backgroundColor ? { backgroundColor: pageStyle.backgroundColor + '33' } : {}),
                    }}
                  >
                    Fondo
                    {pageStyle.backgroundColor && (
                      <span style={{
                        display: 'inline-block', width: 12, height: 12,
                        backgroundColor: pageStyle.backgroundColor,
                        borderRadius: '50%', border: '1px solid #ccc', marginLeft: 6,
                      }} />
                    )}
                  </Button>
                </Tooltip>
                <Button
                  variant={previewMode ? 'contained' : 'outlined'}
                  startIcon={previewMode ? <Icons.Edit /> : <Icons.Visibility />}
                  onClick={() => setPreviewMode((v) => !v)}
                  className={classes.modeToggleBtn}
                  style={previewMode
                    ? { backgroundColor: '#9c27b0', color: '#fff', borderColor: '#9c27b0' }
                    : { borderColor: '#9c27b0', color: '#9c27b0' }
                  }
                >
                  {previewMode ? 'Editar' : 'Vista previa'}
                </Button>
                {!previewMode && (
                  <Button variant="outlined" startIcon={<Icons.AddCircleOutline />} onClick={handleOpenAddSection}
                    style={{ borderColor: '#7b1fa2', color: '#7b1fa2' }}
                  >
                    Agregar sección
                  </Button>
                )}
              </div>

              {/* Panel de fondo de página */}
              {openPageStyle && (
                <div style={{
                  backgroundColor: '#f8f4ff', border: '1px solid #ce93d8',
                  borderRadius: 8, padding: '14px 16px', marginBottom: 12,
                  display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 16,
                }}>
                  <div>
                    <Typography variant="caption" display="block" style={{ color: '#757575', marginBottom: 4, fontWeight: 600 }}>
                      Color de fondo de la página
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="color"
                        value={pageStyle.backgroundColor || '#ffffff'}
                        onChange={(e) => setPageStyle((p) => ({ ...p, backgroundColor: e.target.value }))}
                        style={{ width: 36, height: 36, border: '1px solid #e0e0e0', cursor: 'pointer', borderRadius: 4, padding: 2 }}
                      />
                      <TextField
                        size="small"
                        value={pageStyle.backgroundColor}
                        onChange={(e) => setPageStyle((p) => ({ ...p, backgroundColor: e.target.value }))}
                        placeholder="#ffffff o vacío"
                        inputProps={{ style: { fontFamily: 'monospace', fontSize: 12, width: 130 } }}
                      />
                      {pageStyle.backgroundColor && (
                        <IconButton size="small" onClick={() => setPageStyle((p) => ({ ...p, backgroundColor: '' }))} style={{ color: '#9e9e9e' }}>
                          <Icons.Close fontSize="small" />
                        </IconButton>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {['#ffffff','#f5f5f5','#f8f4ff','#e8f5e9','#e3f2fd','#fff8e1','#fce4ec','#212121','#1a1a2e'].map((c) => (
                        <div key={c} onClick={() => setPageStyle((p) => ({ ...p, backgroundColor: c }))}
                          style={{
                            width: 24, height: 24, borderRadius: 4, backgroundColor: c, cursor: 'pointer',
                            border: pageStyle.backgroundColor === c ? '2px solid #9c27b0' : '1px solid #ccc',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <Typography variant="caption" display="block" style={{ color: '#757575', marginBottom: 4, fontWeight: 600 }}>
                      Imagen de fondo (URL)
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <TextField
                        size="small"
                        value={pageStyle.backgroundImage || ''}
                        onChange={(e) => setPageStyle((p) => ({ ...p, backgroundImage: e.target.value }))}
                        placeholder="https://..."
                        inputProps={{ style: { fontSize: 12, width: 220 } }}
                      />
                      {pageStyle.backgroundImage && (
                        <IconButton size="small" onClick={() => setPageStyle((p) => ({ ...p, backgroundImage: '' }))} style={{ color: '#9e9e9e' }}>
                          <Icons.Close fontSize="small" />
                        </IconButton>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <Divider style={{ marginBottom: 12 }} />

              {isLoading ? (
                <div style={{ textAlign: 'center', padding: 24 }}><CircularProgress size={28} /></div>
              ) : previewMode ? (
                renderPreviewCanvas()
              ) : (
                renderCanvas()
              )}

              {sections.length > 0 && (
                <div style={{ textAlign: 'right', marginTop: 12 }}>
                  <Button variant="contained" color="primary"
                    startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Icons.Save />}
                    onClick={handleSave} disabled={isSaving}
                  >
                    Guardar página
                  </Button>
                </div>
              )}
            </div>
          )}
        </Paper>
      </Grid>

      {/* Dialog: Nueva página */}
      <Dialog open={openNewPage} onClose={() => setOpenNewPage(false)} fullWidth maxWidth="xs">
        <DialogTitle>Nueva página guiada</DialogTitle>
        <DialogContent>
          <TextField label="Nombre de la página" fullWidth autoFocus style={{ marginTop: 8 }}
            value={newPageTitle}
            onChange={(e) => { setNewPageTitle(e.target.value); setNewPageError(''); }}
            helperText='Ej: Inicio, Servicios, Nosotros'
          />
          {newPageError && <Alert severity="error" style={{ marginTop: 12 }}>{newPageError}</Alert>}
        </DialogContent>
        <DialogActions style={{ padding: '10px 24px' }}>
          <Button onClick={() => setOpenNewPage(false)} disabled={isCreatingPage}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleCreatePage}
            disabled={isCreatingPage || !newPageTitle.trim()}
          >
            {isCreatingPage ? <CircularProgress size={20} color="inherit" /> : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Eliminar página */}
      <Dialog open={openDeletePage} onClose={() => !isDeletingPage && setOpenDeletePage(false)} fullWidth maxWidth="xs">
        <DialogTitle>Eliminar página</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Eliminar la página <strong>{currentPageTitle}</strong>? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '10px 24px' }}>
          <Button onClick={() => setOpenDeletePage(false)} disabled={isDeletingPage}>Cancelar</Button>
          <Button variant="contained"
            style={{ backgroundColor: '#c62828', color: '#fff' }}
            onClick={handleDeletePage}
            disabled={isDeletingPage}
          >
            {isDeletingPage ? <CircularProgress size={20} color="inherit" /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Agregar / Editar sección */}
      <Dialog open={openSectionDialog} onClose={() => setOpenSectionDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>
          {editingSectionId ? 'Editar sección' : 'Agregar sección'}
          <Typography variant="caption" display="block" style={{ color: '#9e9e9e' }}>
            {sectionStep === 0 ? 'Paso 1 de 2: Tipo' : 'Paso 2 de 2: Configuración'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {sectionStep === 0 ? renderTypeSelector() : renderSectionConfig()}
        </DialogContent>
        <DialogActions style={{ padding: '10px 24px' }}>
          <Button onClick={() => setOpenSectionDialog(false)}>Cancelar</Button>
          {sectionStep === 1 && !editingSectionId && (
            <Button onClick={() => setSectionStep(0)}>Atrás</Button>
          )}
          {sectionStep === 0 ? (
            <Button variant="contained" color="primary" onClick={handleNextStep}>Siguiente</Button>
          ) : (
            <Button variant="contained" color="primary" onClick={handleConfirmSection}>
              {editingSectionId ? 'Guardar cambios' : 'Agregar sección'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar open={openToast} autoHideDuration={5000} onClose={() => setOpenToast(false)}>
        <Alert onClose={() => setOpenToast(false)} severity={toastSeverity}>{toastMsg}</Alert>
      </Snackbar>
    </Grid>
  );
}

export default GuidedDesignerPage;
